import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client.js';

/** Toll-fraud alerts and holds (docs/39). */

export interface FraudAlert {
  id: string; tenant_id: string; tenant_name: string; tenant_slug: string;
  extension_number: string | null; signal: string; action: 'hold' | 'alert'; source: 'node' | 'control' | 'manual';
  evidence: Record<string, unknown>; detected_at: string;
  reviewed_at: string | null; review_note: string | null; reviewed_by: string | null;
  hold_id: string | null; hold_released_at: string | null;
  rule: string; describe: string; lines: Array<[string, string]>;
}

export interface FraudHold {
  id: string; tenant_id: string; tenant_name: string; tenant_slug: string;
  extension_number: string | null; reason: string; placed_at: string; placed_by: string | null;
  alert_id: string | null; signal: string | null;
  released_at: string | null; released_by: string | null; release_note: string | null;
}

export interface FraudRule { signal: string; action: 'hold' | 'alert'; account?: boolean; label: string; describe: string }

const fk = (...rest: string[]) => ['fraud', ...rest] as const;

export const useFraudAlerts = (f: { tenant?: string | undefined; open?: boolean | undefined }) =>
  useQuery({
    queryKey: fk('alerts', f.tenant ?? '', f.open === undefined ? '' : String(f.open)),
    queryFn: () => {
      const p = new URLSearchParams();
      if (f.tenant) p.set('tenant', f.tenant);
      if (f.open !== undefined) p.set('open', f.open ? '1' : '0');
      return api.get<{ data: FraudAlert[] }>(`/v1/fraud/alerts?${p}`);
    },
    refetchInterval: 30_000,
  });

export const useFraudHolds = (f: { tenant?: string | undefined; live?: boolean | undefined }, enabled = true) =>
  useQuery({
    queryKey: fk('holds', f.tenant ?? '', f.live === false ? '0' : '1'),
    queryFn: () => {
      const p = new URLSearchParams({ live: f.live === false ? '0' : '1' });
      if (f.tenant) p.set('tenant', f.tenant);
      return api.get<{ data: FraudHold[] }>(`/v1/fraud/holds?${p}`);
    },
    enabled,
    refetchInterval: 30_000,
  });

export const useFraudRules = () =>
  useQuery({
    queryKey: fk('rules'),
    queryFn: () => api.get<{ data: FraudRule[] }>('/v1/fraud/rules'),
    staleTime: Infinity,
  });

function useFraudMutation<V>(fn: (v: V) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['fraud'] }); },
  });
}

export const useReleaseHold = () =>
  useFraudMutation(({ id, note }: { id: string; note: string }) => api.post(`/v1/fraud/holds/${id}/release`, { note }));

export const useReviewAlert = () =>
  useFraudMutation(({ id, note }: { id: string; note: string }) => api.post(`/v1/fraud/alerts/${id}/review`, { note }));

export const usePlaceHold = () =>
  useFraudMutation(({ tenantId, ...body }: { tenantId: string; extension_number: string | null; reason: string }) =>
    api.post(`/v1/tenants/${tenantId}/fraud/holds`, body));

export interface TrustedNetwork {
  id: string; tenant_id: string; tenant_name: string; tenant_slug: string;
  network: string; note: string; created_at: string; created_by: string | null;
}

/** Where an account's phones may register from without the location rules judging them (docs/39). */
export const useTrustedNetworks = (enabled = true) =>
  useQuery({
    queryKey: fk('trusted'),
    queryFn: () => api.get<{ data: TrustedNetwork[] }>('/v1/fraud/trusted-networks'),
    enabled,
  });

export const useAddTrustedNetwork = () =>
  useFraudMutation(({ tenantId, ...body }: { tenantId: string; network: string; note: string }) =>
    api.post(`/v1/tenants/${tenantId}/fraud/trusted-networks`, body));

export const useRemoveTrustedNetwork = () =>
  useFraudMutation((id: string) => api.delete(`/v1/fraud/trusted-networks/${id}`));
